import { useMediaQuery, useTheme } from '@material-ui/core'
import { Cancel } from '@material-ui/icons'
import React, { useState } from 'react'
import {
  BooleanInput,
  Create,
  CreateProps,
  ListButton,
  SaveButton,
  SelectInput,
  SimpleForm,
  TextInput,
  Toolbar,
  ToolbarProps,
  useTranslate,
} from 'react-admin'
import { Authority } from '../../../core/auth/Authority'
import { PassageDto } from '../../../core/dto/device/passages/passage.dto'
import { HardwareVersions } from '../../../core/enum/HardwareVersions'
import { ResourceName } from '../../../core/ResourceName'
import FilteredReferenceInput from '../../common/FilteredReferenceInput'
import { useHasAuthority } from '../../hooks/useHasAuthority'
import { SPOM4FullAddressInput } from './SPOM4Address'

const validatePassageCreate = (values: PassageDto) => {
  const errors: { [n: string]: string } = {}
  if (!values.name) {
    errors.name = 'ra.validation.required'
  }
  if (values.hardwareVersion === HardwareVersions.SPOM4) {
    const addressParts = values.tsCommAddress?.split('&')
    if (!addressParts?.[0]) {
      errors.spom4_ipGate = 'ra.validation.required'
    }
    if (!addressParts?.[1]) {
      errors.spom4_tscanHost = 'ra.validation.required'
    }
    if (!addressParts?.[2]) {
      errors.spom4_direction = 'ra.validation.required'
    }
    if (!addressParts?.[3]) {
      errors.spom4_rs485Address = 'ra.validation.required'
    }
  }
  if (values.hardwareVersion === HardwareVersions.MODULO5 && !values.tsCanId) {
    errors.tsCanId = 'ra.validation.required'
  }
  if (Number.isNaN(Number(values.freeEntrancesCount)))
    errors.freeEntrancesCount = 'ra.validation.not_numeric'
  if (Number.isNaN(Number(values.freeExitsCount)))
    errors.freeExitsCount = 'ra.validation.not_numeric'
  if (Number.isNaN(Number(values.ticketEntrancesCount)))
    errors.ticketEntrancesCount = 'ra.validation.not_numeric'
  if (Number.isNaN(Number(values.ticketExitsCount)))
    errors.ticketExitsCount = 'ra.validation.not_numeric'
  if (Number.isNaN(Number(values.unauthorizedEntrancesCount)))
    errors.unauthorizedEntrancesCount = 'ra.validation.not_numeric'
  if (Number.isNaN(Number(values.unauthorizedExitsCount)))
    errors.unauthorizedExitsCount = 'ra.validation.not_numeric'
  return errors
}

const CreateTitle = () => {
  const translate = useTranslate()
  return <span>{translate('resources.passages.titles.create')}</span>
}

const CreateToolbar = (props: ToolbarProps) => (
  <Toolbar {...props}>
    <SaveButton />
    <ListButton icon={<Cancel />} label="const.cancel" />
  </Toolbar>
)

export const PassageCreate = (props: CreateProps) => {
  const theme = useTheme()
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const hasAuthority = useHasAuthority()
  const { record: passage } = props
  const [hardwareVersion, setHardwareVersion] = useState(
    passage?.hardwareVersion
      ? passage?.hardwareVersion
      : HardwareVersions.MODULO5,
  )

  const defaultValues = {
    hardwareVersion: HardwareVersions.MODULO5,
  }

  const TSCanInput = ({ ...p }) =>
    hasAuthority(Authority.VIEW_TS_CANS) ? (
      <FilteredReferenceInput
        {...p}
        source="tsCanId"
        defaultValue={null}
        reference={ResourceName.TS_CANS}
        sort={{ field: 'name', order: 'ASC' }}
        perPage={smallScreen ? 5 : 15}
        allowEmpty
        filterSource="search"
        selectWithPaginationInputProps={{
          optionText: 'name',
          showFilter: true,
        }}
      />
    ) : (
      <div />
    )

  return (
    <Create {...props} title={<CreateTitle />}>
      <SimpleForm
        redirect="show"
        validate={validatePassageCreate}
        toolbar={<CreateToolbar />}
        initialValues={defaultValues}
        transform={(formData) => ({
          ...formData,
          tsCommAddress:
            hardwareVersion === HardwareVersions.SPOM4
              ? formData?.tsCommAddress
              : undefined,
        })}
      >
        <TextInput source="name" isRequired />
        <BooleanInput source="isWatched" />
        {hasAuthority(Authority.VIEW_DEVICE_CATEGORIES) && (
          <FilteredReferenceInput
            source="categoryId"
            defaultValue={null}
            reference={ResourceName.DEVICE_CATEGORIES}
            sort={{ field: 'name', order: 'ASC' }}
            perPage={smallScreen ? 5 : 15}
            allowEmpty
            filterSource="search"
            selectWithPaginationInputProps={{
              optionText: 'hierarchyString',
              showFilter: true,
            }}
          />
        )}
        {hasAuthority(Authority.VIEW_CAMERAS) && (
          <FilteredReferenceInput
            source="cameraId"
            defaultValue={null}
            reference={ResourceName.CAMERAS}
            sort={{ field: 'description', order: 'ASC' }}
            perPage={smallScreen ? 5 : 15}
            allowEmpty
            filterSource="search"
            selectWithPaginationInputProps={{
              optionText: 'description',
              showFilter: true,
            }}
          />
        )}
        <TextInput source="freeEntrancesCount" defaultValue={0} />
        <TextInput source="freeExitsCount" defaultValue={0} />
        <TextInput source="ticketEntrancesCount" defaultValue={0} />
        <TextInput source="ticketExitsCount" defaultValue={0} />
        <TextInput source="unauthorizedEntrancesCount" defaultValue={0} />
        <TextInput source="unauthorizedExitsCount" defaultValue={0} />
        <SelectInput
          value={hardwareVersion}
          onChange={(e) => setHardwareVersion(e.target.value)}
          source="hardwareVersion"
          choices={Object.entries(HardwareVersions).map(([value]) => ({
            id: value,
            name: `resources.enums.hardwareVersions.${value}`,
          }))}
        />
        {hardwareVersion === HardwareVersions.SPOM4 && (
          <SPOM4FullAddressInput source="tsCommAddress" />
        )}
        {hardwareVersion === HardwareVersions.MODULO5 && (
          <TSCanInput source="tsCommAddress" />
        )}
      </SimpleForm>
    </Create>
  )
}
